// Admin dashboard for the automated B2B email sequences (J0 / J+5 / J+10 / J+20).
// Reads `crm_contacts.prospect_sequence` to surface KPIs, the funnel, the
// list of contacts in flight and a history of recent sends.
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Mail, RefreshCw, PlayCircle, Loader2, AlertTriangle, Clock, CheckCircle2, Sparkles } from "lucide-react";
import { adminApi } from "@/lib/adminApi";

const CATEGORY_LABELS = {
  domaines_viticoles: "Domaines viticoles",
  cavistes: "Cavistes",
  negociants: "Négociants",
  restaurateurs: "Restaurateurs",
  distributeurs: "Distributeurs",
  importateurs: "Importateurs",
  ecommerce: "E-commerce",
  ecoles_du_vin: "Écoles du vin",
  drinks_tech: "Drinks Tech",
  autre: "Autre",
};

function KpiCard({ icon: Icon, label, value, sub, accent = "ink", testId }) {
  const accentClass = {
    ink: "text-[color:var(--gv-ink)]",
    pink: "text-[color:var(--gv-pink)]",
    amber: "text-amber-600",
    green: "text-emerald-600",
  }[accent];
  return (
    <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-4" data-testid={testId}>
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-wide text-[color:var(--gv-ink)]/60 mb-1">
        {Icon && <Icon size={13} />} {label}
      </div>
      <div className={`text-3xl font-display tabular-nums ${accentClass}`}>{value}</div>
      {sub && <div className="text-[11px] text-[color:var(--gv-ink)]/50 mt-1">{sub}</div>}
    </div>
  );
}

function formatDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  // Affichage en heure locale Paris (le navigateur le fait via fr-FR + timeZone explicite)
  return d.toLocaleString("fr-FR", {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    timeZone: "Europe/Paris",
  });
}

function StepPill({ step }) {
  const map = { 1: { label: "J0", c: "bg-rose-100 text-rose-700" }, 2: { label: "J+5", c: "bg-amber-100 text-amber-700" }, 3: { label: "J+10", c: "bg-sky-100 text-sky-700" }, 4: { label: "J+20", c: "bg-violet-100 text-violet-700" } };
  const m = map[step] || { label: step, c: "bg-gray-100 text-gray-700" };
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${m.c}`}>{m.label}</span>;
}

const AdminEmailSequencesPage = () => {
  const [overview, setOverview] = useState(null);
  const [active, setActive] = useState({ items: [], total: 0 });
  const [history, setHistory] = useState({ items: [] });
  const [filterStep, setFilterStep] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const [o, a, h] = await Promise.all([
        adminApi.emailOverview(),
        adminApi.emailActive({ step: filterStep, category: filterCat, limit: 100 }),
        adminApi.emailHistory(50),
      ]);
      setOverview(o);
      setActive(a);
      setHistory(h);
    } finally { setLoading(false); }
  };

  /* eslint-disable */
  useEffect(() => { refresh(); }, []);
  useEffect(() => { refresh(); }, [filterStep, filterCat]);
  /* eslint-enable */

  const runNow = async () => {
    if (!window.confirm("Lancer immédiatement le cron des séquences (hors du run quotidien à 09:00 UTC) ?")) return;
    setRunning(true); setRunResult(null);
    try {
      const r = await adminApi.emailRunNow();
      setRunResult(r);
      await refresh();
    } catch (e) { alert(`Erreur : ${e.message}`); }
    finally { setRunning(false); }
  };

  const runBetaTick = async () => {
    if (!window.confirm("Lancer les étapes J+3 / J+7 du programme Bêta dont la date est échue ?")) return;
    setRunning(true);
    try {
      const r = await adminApi.betaOnboardingTick();
      const errMsg = r.errors?.length ? `\n⚠️ ${r.errors.length} erreur(s)` : "";
      alert(`✓ ${r.sent_count} email(s) Onboarding Bêta envoyé(s)${errMsg}`);
    } catch (e) { alert(`Erreur : ${e.message}`); }
    finally { setRunning(false); }
  };

  const fmtPct = (n, d) => (d ? `${Math.round((n / d) * 100)}%` : "—");

  const replyRate = useMemo(() => {
    if (!overview) return 0;
    const denom = overview.replied + overview.completed + overview.stopped_manual + overview.active;
    return denom ? Math.round((overview.replied / denom) * 100) : 0;
  }, [overview]);

  if (loading && !overview) {
    return <div className="p-10 text-sm text-[color:var(--gv-ink)]/60" data-testid="admin-email-loading"><Loader2 className="inline animate-spin mr-2" size={14}/> Chargement…</div>;
  }

  return (
    <div data-testid="admin-email-sequences" className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-4xl text-[color:var(--gv-ink)] mb-1 flex items-center gap-2"><Mail size={24}/> Séquences emails B2B</h1>
          <p className="text-sm text-[color:var(--gv-ink)]/60">
            J0 → J+5 → J+10 → J+20 · <strong>envoi manuel uniquement</strong> · clique « Démarrer séquence » dans le CRM ou « Run cron now » ici · throttle 10 J0 fresh max par batch
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/admin/email/templates" className="btn-secondary text-sm" data-testid="email-templates-link">Templates &amp; traductions</Link>
          <button onClick={runBetaTick} disabled={running} className="btn-secondary text-sm disabled:opacity-50" data-testid="beta-onboarding-tick" title="Envoyer les étapes J+3 / J+7 du programme Bêta dues">
            {running ? <Loader2 className="animate-spin" size={14}/> : <Sparkles size={14}/>} Onboarding Bêta
          </button>
          <button onClick={refresh} className="btn-secondary text-sm" data-testid="email-refresh"><RefreshCw size={14}/> Refresh</button>
          <button onClick={runNow} disabled={running} className="btn-primary text-sm disabled:opacity-50" data-testid="email-run-now">
            {running ? <Loader2 className="animate-spin" size={14}/> : <PlayCircle size={14}/>} Run cron now
          </button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="email-kpis">
        <KpiCard icon={Mail} label="Actives" value={overview.active} accent="pink" testId="kpi-active" />
        <KpiCard icon={Clock} label="À envoyer < 24h" value={overview.scheduled_next_24h} sub={overview.overdue ? `${overview.overdue} en retard` : null} accent={overview.overdue ? "amber" : "ink"} testId="kpi-scheduled" />
        <KpiCard label="Envoyés 7j" value={overview.sent_last_7d} sub={`${overview.sent_last_30d} sur 30j`} testId="kpi-sent" />
        <KpiCard icon={CheckCircle2} label="Complétées" value={overview.completed} accent="green" testId="kpi-completed" />
        <KpiCard label="Réponses" value={overview.replied} sub={`${replyRate}% reply rate`} accent="green" testId="kpi-replied" />
        <KpiCard label="Arrêtées" value={overview.stopped_manual} testId="kpi-stopped" />
      </div>

      {/* Funnel by next step + by category */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5 lg:col-span-2" data-testid="email-funnel">
          <h3 className="font-display text-base mb-3">Prochaine étape à envoyer</h3>
          <div className="space-y-2">
            {overview.active_by_next_step.map((row) => {
              const max = Math.max(...overview.active_by_next_step.map((r) => r.count), 1);
              const pct = Math.round((row.count / max) * 100);
              return (
                <div key={row.step} className="flex items-center gap-3" data-testid={`funnel-step-${row.step}`}>
                  <div className="w-12"><StepPill step={row.step} /></div>
                  <div className="flex-1 h-7 bg-[color:var(--gv-line)]/30 rounded-[3px] overflow-hidden">
                    <div className="h-full bg-[color:var(--gv-pink)]/80 flex items-center px-2 text-[12px] text-white tabular-nums transition-all" style={{ width: `${pct}%` }}>
                      {row.count > 0 && row.count}
                    </div>
                  </div>
                  <div className="w-12 text-right text-[12px] tabular-nums text-[color:var(--gv-ink)]/60">{row.count}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-4 pt-4 border-t border-[color:var(--gv-line)]">
            <h4 className="text-[11px] uppercase tracking-wide text-[color:var(--gv-ink)]/60 mb-2">Étape atteinte (cumul history)</h4>
            <div className="flex items-center gap-2 flex-wrap text-[12px]">
              {overview.current_step_funnel.map((row) => (
                <div key={row.step} className="inline-flex items-center gap-1.5 px-2 py-1 rounded-[3px] bg-[color:var(--gv-mist)]/50" data-testid={`history-step-${row.step}`}>
                  <StepPill step={row.step} /> <span className="tabular-nums">{row.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid="email-by-category">
          <h3 className="font-display text-base mb-3">Par catégorie</h3>
          {overview.by_category.length === 0 ? (
            <div className="text-xs text-[color:var(--gv-ink)]/40">Aucune séquence active</div>
          ) : (
            <ul className="space-y-1.5">
              {overview.by_category.map((row) => (
                <li key={row.category} className="flex items-center justify-between text-[13px]" data-testid={`cat-${row.category}`}>
                  <span className="text-[color:var(--gv-ink)]/80">{CATEGORY_LABELS[row.category] || row.category}</span>
                  <span className="tabular-nums font-semibold">{row.count}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Run result toast */}
      {runResult && (
        <div className="border border-emerald-200 bg-emerald-50 rounded-[4px] p-3 text-[13px]" data-testid="email-run-result">
          ✓ Cron exécuté : <strong>{runResult.sent}</strong> envoyés
          {Object.entries(runResult.by_step || {}).filter(([, v]) => v > 0).map(([s, v]) => ` · ${STEP_LABEL(parseInt(s))}: ${v}`).join("")}
          {runResult.errors > 0 && <span className="text-rose-700"> · {runResult.errors} erreurs</span>}
          {runResult.j0_throttled > 0 && <span className="text-amber-700"> · {runResult.j0_throttled} J0 throttled</span>}
        </div>
      )}

      {/* Filters + active list */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid="email-active-list">
        <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
          <h3 className="font-display text-base">Contacts en séquence active ({active.total})</h3>
          <div className="flex items-center gap-2">
            <select value={filterStep} onChange={(e) => setFilterStep(e.target.value)} className="border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-xs bg-white" data-testid="filter-step">
              <option value="">Toutes étapes</option>
              <option value="1">J0</option>
              <option value="2">J+5</option>
              <option value="3">J+10</option>
              <option value="4">J+20</option>
            </select>
            <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} className="border border-[color:var(--gv-line)] rounded-[3px] px-2 py-1.5 text-xs bg-white" data-testid="filter-category">
              <option value="">Toutes catégories</option>
              {Object.entries(CATEGORY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
        </div>

        {active.items.length === 0 ? (
          <div className="text-center text-sm text-[color:var(--gv-ink)]/40 py-8" data-testid="email-active-empty">
            Aucune séquence active selon les filtres choisis.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]" data-testid="email-active-table">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[color:var(--gv-ink)]/60 border-b border-[color:var(--gv-line)]">
                  <th className="py-2 pr-3">Contact</th>
                  <th className="py-2 pr-3">Catégorie</th>
                  <th className="py-2 pr-3">Étape</th>
                  <th className="py-2 pr-3">Prochain envoi</th>
                  <th className="py-2 pr-3">Dernier envoi</th>
                  <th className="py-2 pr-3">Score</th>
                </tr>
              </thead>
              <tbody>
                {active.items.map((row) => {
                  const overdue = row.next_step_at && new Date(row.next_step_at) < new Date();
                  return (
                    <tr key={row.id} className="border-b border-[color:var(--gv-line)]/40 hover:bg-[color:var(--gv-mist)]/30">
                      <td className="py-2 pr-3">
                        <Link to={`/admin/crm/${row.id}`} className="text-[color:var(--gv-ink)] hover:text-[color:var(--gv-pink)] font-medium" data-testid={`row-link-${row.id}`}>
                          {row.name || `${row.first_name || ""} ${row.last_name || ""}`.trim() || row.email}
                        </Link>
                        <div className="text-[11px] text-[color:var(--gv-ink)]/50">{row.company || row.email}</div>
                      </td>
                      <td className="py-2 pr-3 text-[color:var(--gv-ink)]/70">{CATEGORY_LABELS[row.category] || row.category || "—"}</td>
                      <td className="py-2 pr-3"><StepPill step={row.next_step} /></td>
                      <td className={`py-2 pr-3 tabular-nums ${overdue ? "text-amber-700 font-semibold" : "text-[color:var(--gv-ink)]/80"}`}>
                        {overdue && <AlertTriangle size={11} className="inline mr-1" />}
                        {formatDate(row.next_step_at)}
                      </td>
                      <td className="py-2 pr-3 text-[color:var(--gv-ink)]/60 tabular-nums">{formatDate(row.last_sent_at)}</td>
                      <td className="py-2 pr-3 tabular-nums text-[color:var(--gv-pink)] font-semibold">{row.lead_score || 0}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent sends */}
      <div className="bg-white border border-[color:var(--gv-line)] rounded-[5px] p-5" data-testid="email-history">
        <h3 className="font-display text-base mb-3">Historique récent (50 derniers envois)</h3>
        {history.items.length === 0 ? (
          <div className="text-center text-sm text-[color:var(--gv-ink)]/40 py-6">Aucun envoi enregistré.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[color:var(--gv-ink)]/60 border-b border-[color:var(--gv-line)]">
                  <th className="py-2 pr-3">Quand</th>
                  <th className="py-2 pr-3">Contact</th>
                  <th className="py-2 pr-3">Étape</th>
                  <th className="py-2 pr-3">Catégorie</th>
                  <th className="py-2 pr-3">OK</th>
                </tr>
              </thead>
              <tbody>
                {history.items.map((row, i) => (
                  <tr key={i} className="border-b border-[color:var(--gv-line)]/40">
                    <td className="py-2 pr-3 tabular-nums text-[color:var(--gv-ink)]/70">{formatDate(row.at)}</td>
                    <td className="py-2 pr-3">
                      <Link to={`/admin/crm/${row.contact_id}`} className="hover:text-[color:var(--gv-pink)]">{row.name || row.email}</Link>
                      <div className="text-[11px] text-[color:var(--gv-ink)]/50">{row.email}</div>
                    </td>
                    <td className="py-2 pr-3"><StepPill step={row.step} /></td>
                    <td className="py-2 pr-3 text-[color:var(--gv-ink)]/70">{CATEGORY_LABELS[row.category] || row.category}</td>
                    <td className="py-2 pr-3">{row.ok ? <CheckCircle2 size={14} className="text-emerald-600"/> : <AlertTriangle size={14} className="text-rose-600"/>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

// inline label helper used in run result toast
function STEP_LABEL(s) { return { 1: "J0", 2: "J+5", 3: "J+10", 4: "J+20" }[s] || `Step ${s}`; }

export default AdminEmailSequencesPage;
